import "./cpl-applicant-submitted-profile.scss";
import { useState, useContext, useEffect, useRef } from "react";
import ButtonKMQ from "../../UI/Button/ButtonKMQ";
import { IPortfolio } from "../../../hooks/Context/PortfolioContext";
import moment from "moment";
import { ArrowCounterClockwise, PencilSimple, WarningCircle } from "@phosphor-icons/react";
import { ButtonTypes } from "../../../constants/ui/types/ButtonTypes";
import { useMutateRecommendation } from "../../../api/normalization/useMutateRecommendation";
import { UserContext } from "../../../hooks/Context/UserContext";
import { ResumeContext } from "../../../hooks/Context/ResumeContext";
import { PortfolioContext } from "../../../hooks/Context/PortfolioContext";
import { useQueryUserInfo } from "../../../api/admin/useQueryUserInfo";
import { portfolioToResume } from "../../../lib/data-formatting/portfolioToResume";
import { resumeToProfile } from "../../../lib/data-formatting/resumeToProfile";
import Loading from "../../UI/Loading/Loading";
import { countries } from "countries-list";
import { useMutateNormalization } from "../../../api/normalization/useQueryNormalization";
import AlertModal from "../../UI/Modals/AlertModal/AlertModal";
import { CurrOptionsType } from "../../../lib/resume-parser/redux/types";
import { IAttachment } from "../CplApplicantProfile/components/FileUpload";
import { useNavigate } from "react-router-dom";
import SubmittedWorkExperienceSection from "./SubmittedWorkExperienceSection";
import SubmittedEducationSection from "./SubmittedEducationSection";
import SubmittedCertificationSection from "./SubmittedCertificationSection";

interface CplApplicantSubmittedProfileProps {
  portfolio: IPortfolio;
  certificatesEnabled?: boolean;
  onEditModeChanged?: (isEditing: boolean) => void;
}

const CplApplicantSubmittedProfile = ({
  portfolio,
  certificatesEnabled = false,
  onEditModeChanged,
}: CplApplicantSubmittedProfileProps) => {
  const [editMode, setEditMode] = useState(false);
  const [editedPortfolio, setEditedPortfolio] = useState<IPortfolio>(portfolio);
  const [currOptions, setCurrOptions] = useState<CurrOptionsType>({ jobs: [], educations: [] });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showEditAlert, setShowEditAlert] = useState<boolean>(false);
  const [showUploadResumeAlert, setShowUploadResumeAlert] = useState<boolean>(false);
  const [user] = useContext(UserContext);
  const [parsedResume, setParsedResume] = useContext(ResumeContext);
  const [portfolioState, setPortfolio] = useContext(PortfolioContext);
  const { refetch } = useQueryUserInfo({});
  const normalizationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const { mutate: postNormalization } = useMutateNormalization(setCurrOptions);
  const locationOptions = [
    { label: 'Canada', value: 'Canada' },
    { label: 'United States', value: 'United States' },
    ...Object.values(countries).sort((a, b) => a.name.localeCompare(b.name)).map(item => ({
      label: item.name,
      value: item.name,
    })),
  ];

  const institutionTypeOptions = [
    { label: "University", value: "University" },
    { label: "College", value: "College" },
    { label: "Technical School", value: "Technical School" },
    { label: "High School", value: "High School" },
    { label: "Other", value: "Other" },
  ];

  const getDegreeTypeOptions = (institutionType: string) => {
    if (institutionType === "University") {
      return [
        { label: "Bachelor's", value: "Bachelor's" },
        { label: "Master's", value: "Master's" },
        { label: "Doctoral", value: "Doctoral" },
        { label: "Associate's", value: "Associate's" },
        { label: "Certificate", value: "Certificate" },
      ];
    } else if (institutionType === "College") {
      return [
        { label: "Associate's", value: "Associate's" },
        { label: "Certificate", value: "Certificate" },
        { label: "Diploma", value: "Diploma" },
      ];
    } else if (institutionType === "Technical School") {
      return [
        { label: "Certificate", value: "Certificate" },
        { label: "Diploma", value: "Diploma" },
      ];
    } else if (institutionType === "High School") {
      return [
        { label: "Diploma", value: "Diploma" },
        { label: "GED", value: "GED" },
      ];
    } else {
      return [
        { label: "Certificate", value: "Certificate" },
        { label: "Diploma", value: "Diploma" },
        { label: "Other", value: "Other" },
      ];
    }
  };

  const monthOptions = moment.months().map(month => ({
    label: month,
    value: month
  }));

  const getYearOptions = () => {
    const currentYear = moment().year();
    const years = [];
    for (let year = currentYear; year >= currentYear - 80; year--) {
      years.push({ label: year.toString(), value: year.toString() });
    }
    return years;
  };

  const {
    mutate: postRecommendation,
    isSuccess: isRecommendationSuccess,
    isPending: isRecommendationLoading,
    isError: isRecommendationError,
    data: dataRecommendation
  } = useMutateRecommendation({
    userIsSoft: user.isSoft
  });

  useEffect(() => {
    if (editMode) {
      const portfolioWithTitles = { ...editedPortfolio };
      if (portfolioWithTitles.educations) {
        portfolioWithTitles.educations = portfolioWithTitles.educations.map(edu => ({
          ...edu,
          title: edu.program
        }));
      }
      postNormalization(portfolioWithTitles);
    }
    return () => {
      if (normalizationTimeoutRef.current) {
        clearTimeout(normalizationTimeoutRef.current);
      }
    };
  }, [editMode]);

  useEffect(() => {
    setEditedPortfolio(portfolio);
  }, [portfolio]);

  useEffect(() => {
    if (onEditModeChanged) {
      onEditModeChanged(editMode);
    }
  }, [editMode, onEditModeChanged]);

  useEffect(() => {
    if (isRecommendationSuccess) {
      if (!user.isSoft) {
        refetch().then((response) => {
          if (response.data) {
            const updatedPortfolio = portfolioToResume(response.data);
            setPortfolio({
              ...updatedPortfolio,
              start_over: portfolioState.start_over,
            });

            setParsedResume(updatedPortfolio);
          }
        });
      } else {
        setPortfolio({
          ...resumeToProfile(parsedResume),
          id: dataRecommendation?.portfolio_id,
          start_over: portfolioState.start_over,
        });
      }
      setEditMode(false);
      navigate('/cpl-applicant/my-programs');
    }
  }, [isRecommendationSuccess]);

const debouncedNormalization = () => {
  if (normalizationTimeoutRef.current) {
    clearTimeout(normalizationTimeoutRef.current);
  }

  normalizationTimeoutRef.current = setTimeout(() => {
    const portfolioWithTitles = { ...editedPortfolio };
    if (portfolioWithTitles.educations) {
      portfolioWithTitles.educations = portfolioWithTitles.educations.map(edu => ({
        ...edu,
        title: edu.program
      }));
    }
    
    setEditedPortfolio(currentState => {
      const latestPortfolio = { ...currentState };
      if (latestPortfolio.educations) {
        latestPortfolio.educations = latestPortfolio.educations.map(edu => ({
          ...edu,
          title: edu.program
        }));
      }
      
      postNormalization(latestPortfolio);
      return currentState;
    });
  }, 500);
};

  const validateForm = (): boolean => {
    const isValidWork = editedPortfolio.jobs?.every(job => Boolean(
      job?.title &&
      job.company &&
      job.start_date &&
      job.description &&
      (job.current_job || job.end_date) &&
      job.location &&
      job.normalization_choice
    )) ?? false;

    const isValidEducation = editedPortfolio.educations?.every(edu => Boolean(
      edu?.program &&
      edu.institution_name &&
      edu.start_date &&
      edu.institution_type &&
      edu.location
    )) ?? false;

    if (!certificatesEnabled) return isValidWork && isValidEducation;

    if (!editedPortfolio.certificates?.length) return isValidWork && isValidEducation;

    const isValidCertificate = editedPortfolio.certificates?.every(cert => Boolean(
      cert?.name &&
      cert.certificate_issuer &&
      cert.issue_date &&
      cert.description
    )) ?? false;

    return isValidWork && isValidEducation && isValidCertificate;
  };

  const handleEditClick = () => {
    setShowEditAlert(true);
  };

  const handleConfirmEdit = () => {
    setShowEditAlert(false);
    setEditedPortfolio(prevState => {
      if (prevState.educations) {
        const updatedEducations = prevState.educations.map(edu => {
          // @ts-ignore
          if (edu.normalized && !edu.normalization_choice) {
            return {
              ...edu,
              // @ts-ignore
              normalization_choice: edu.normalized
            };
          }
          // @ts-ignore
          else if (edu.normalization_choice && !edu.normalized) {
            return {
              ...edu,
              normalized: edu.normalization_choice
            };
          }
          return edu;
        });
        
        return {
          ...prevState,
          educations: updatedEducations
        };
      }
      return prevState;
    });
    
    setEditMode(true);
  };

  const handleUploadResumeClick = () => {
    setShowUploadResumeAlert(true);
  };

  const handleConfirmUploadResume = () => {
    setShowUploadResumeAlert(false);
    handleUploadResume();
  };

  const handleUploadResume = () => {
    setPortfolio({ ...portfolioState, start_over: true });
    navigate("/cpl-applicant");
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const updatedProfile = {
      ...editedPortfolio
    };

    if (updatedProfile.jobs && updatedProfile.jobs.length > 0) {
      updatedProfile.jobs = updatedProfile.jobs.map(job => ({
        ...job,
        // @ts-ignore
        related_documents: job.related_documents ? job.related_documents.map(doc => doc.id) : []
      }));
    }
  
    if (updatedProfile.educations && updatedProfile.educations.length > 0) {
      updatedProfile.educations = updatedProfile.educations.map((edu: any) => ({
        ...edu,
        title: edu.program,
        // @ts-ignore
        related_documents: edu.related_documents ? edu.related_documents.map(doc => doc.id) : []
      }));
    }
  
    if (updatedProfile.certificates && updatedProfile.certificates.length > 0) {
      updatedProfile.certificates = updatedProfile.certificates.map(cert => {
        const updated = {
          ...cert,
          // @ts-ignore
          related_documents: cert.related_documents ? cert.related_documents.map(doc => doc.id) : []
        };
  
        if (cert.issue_date) {
          const isoDate = moment(cert.issue_date).format('YYYY-MM-DDT00:00:00Z');
          updated.issue_date = isoDate;
        }
  
        return updated;
      });
    }
    
    // @ts-ignore
    if (!updatedProfile.basic_info && (updatedProfile.portfolio_user_name || parsedResume?.basic_info)) {
      const fullName = parsedResume?.basic_info?.first_name && parsedResume?.basic_info?.last_name
        ? `${parsedResume.basic_info.first_name} ${parsedResume.basic_info.last_name}`
        : updatedProfile.portfolio_user_name || "";
      
      // @ts-ignore
      const email = parsedResume?.basic_info?.email || updatedProfile.portfolio_user_email || "";
      // @ts-ignore
      updatedProfile.basic_info = {
        full_name: fullName,
        email: email
      };
    }

    postRecommendation({
      data: {
        profile: updatedProfile,
        portfolio_id: updatedProfile.id,
        resume_storage_name: parsedResume.resume_storage_name
      },
    });
  };

  const handleCancel = () => {
    setEditedPortfolio(portfolio);
    setEditMode(false);
  };

  const handleChooseNormalization = (jobIndex: number, optionTitle: string) => {
    if (optionTitle === "None of the Above") {
      setShowModal(true);
      updateJob(jobIndex, 'normalization_choice', optionTitle);
    } else {
      updateJob(jobIndex, 'normalization_choice', optionTitle);
    }
  };

  const handleSelectEducationNormalization = (eduIndex: number, value: string) => {
    updateEducation(eduIndex, 'normalized', value);
    updateEducation(eduIndex, 'normalization_choice', value);
  };

  const updatePortfolioField = <T,>(
    collection: T[] | undefined,
    index: number,
    field: string,
    value: any,
    defaultItem: Partial<Record<string, any>>
  ): T[] => {
    const result = [...(collection || [])] as T[];
    if (!result[index]) {
      result[index] = defaultItem as unknown as T;
    }
    result[index] = { ...result[index], [field]: value };
    return result;
  };

  const updateJob = (index: number, field: string, value: any) => {
    setEditedPortfolio(prevState => {
      const jobs = updatePortfolioField(
        prevState.jobs,
        index,
        field,
        value,
        { title: "", company: "", description: "", start_date: "" }
      );

      if (field === 'title' || field === 'description') {
        debouncedNormalization();
      }

      return { ...prevState, jobs };
    });
  };

  const updateEducation = (index: number, field: string, value: any) => {
    setEditedPortfolio(prevState => {
      const educations = updatePortfolioField(
        prevState.educations,
        index,
        field,
        value,
        { program: "", institution_name: "", start_date: "" }
      );

      if (field === 'program' || field === 'institution_name' || field === 'institution_type') {
        debouncedNormalization();
      }

      return { ...prevState, educations };
    });
  };

  const updateCertificate = (index: number, field: string, value: any) => {
    setEditedPortfolio(prevState => {
      const certificates = updatePortfolioField(
        prevState.certificates,
        index,
        field,
        value,
        { name: "", certificate_issuer: "", issue_date: "", description: "" }
      );
      return { ...prevState, certificates };
    });
  };

  const handleResumeJobsDateChange = (newDateInfo: any, index: number) => {
    if (editMode) {
      if (newDateInfo.start_date) {
        updateJob(index, 'start_date', newDateInfo.start_date);
      }
      if (newDateInfo.end_date) {
        updateJob(index, 'end_date', newDateInfo.end_date);
      }
    }
  };

  const handleResumeEducationDateChange = (newDateInfo: any, index: number) => {
    if (editMode) {
      if (newDateInfo.start_date) {
        updateEducation(index, 'start_date', newDateInfo.start_date);
      }
      if (newDateInfo.end_date) {
        updateEducation(index, 'end_date', newDateInfo.end_date);
      }
    }
  };

  const mapApiDocumentsToAttachments = (documents = []) => {
    if (!documents || documents.length === 0) return [];

    return documents.map((doc: IAttachment) => ({
      id: doc.id,
      // @ts-ignore
      name: doc?.title ? doc.title : doc.name,
      description: doc.description || '',
      saved: doc.saved === undefined ? true : doc.saved,
      edit: doc.edit === undefined ? false : doc.edit,
      uniqueId: `doc-${doc.id}`,
      file: doc.file
    }));
  };

  return (
    <div className="submitted-profile">
      <Loading loading={isRecommendationLoading} showProgress text={'Generating recommendations'} />

      <div className="edit-button-container">
        {!editMode ? (
          <div onClick={handleEditClick} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <PencilSimple size={16} />
            <span style={{ marginLeft: '5px' }}>Edit</span>
          </div>
        ) : null}
      </div>

      <div className="edit-resume-review">
        <SubmittedWorkExperienceSection 
          jobs={editedPortfolio.jobs || []}
          editMode={editMode}
          locationOptions={locationOptions}
          currOptions={currOptions}
          isRecommendationError={isRecommendationError}
          updateJob={updateJob}
          handleChooseNormalization={handleChooseNormalization}
          handleResumeJobsDateChange={handleResumeJobsDateChange}
          // @ts-ignore
          mapApiDocumentsToAttachments={mapApiDocumentsToAttachments}
        />

        <SubmittedEducationSection 
          educations={editedPortfolio.educations || []}
          editMode={editMode}
          locationOptions={locationOptions}
          institutionTypeOptions={institutionTypeOptions}
          currOptions={currOptions}
          isRecommendationError={isRecommendationError}
          updateEducation={updateEducation}
          handleSelectEducationNormalization={handleSelectEducationNormalization}
          handleResumeEducationDateChange={handleResumeEducationDateChange}
          // @ts-ignore
          mapApiDocumentsToAttachments={mapApiDocumentsToAttachments}
          getDegreeTypeOptions={getDegreeTypeOptions}
        />

        {certificatesEnabled && (
          <SubmittedCertificationSection 
            certificates={editedPortfolio.certificates}
            editMode={editMode}
            updateCertificate={updateCertificate}
            // @ts-ignore
            mapApiDocumentsToAttachments={mapApiDocumentsToAttachments}
            monthOptions={monthOptions}
            getYearOptions={getYearOptions}
          />
        )}
      </div>

      {editMode && (
        <div className="edit-buttons-container" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <ButtonKMQ
              onClick={handleSubmit}
              type={ButtonTypes.Tertiary}
              disabled={!validateForm() || isRecommendationLoading}
            >
              Save
            </ButtonKMQ>
            <ButtonKMQ
              onClick={handleCancel}
            >
              Cancel
            </ButtonKMQ>
          </div>
          <ButtonKMQ
            onClick={handleUploadResumeClick}
            type={ButtonTypes.Secondary}
          >
            <div className="button-with-icon">
              <ArrowCounterClockwise size={25} />
              <span className="text">Upload different Resume</span>
          </div>
          </ButtonKMQ>
        </div>
      )}

      <AlertModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onProceed={() => setShowModal(false)}
        text="Can't find what you're looking for? Try editing your job title and description. This will generate new titles for you to choose from based on the most relevant options."
        proceedText="Close"
        icon={<WarningCircle size={56} color={'#212121'} />}
      />

      <AlertModal
        open={showEditAlert}
        onClose={() => setShowEditAlert(false)}
        text="Please be aware that making changes to your resume may generate new recommendations, and you will lose any previous ones. Please carefully review your edits before submitting."
        proceedText="Yes, proceed"
        onProceed={handleConfirmEdit}
        closeText="No, cancel"
        title="You Are About to Edit Your Resume!"
        icon={<WarningCircle size={56} color="#212121" />}
      />

      <AlertModal
        open={showUploadResumeAlert}
        onClose={() => setShowUploadResumeAlert(false)}
        text="Uploading new resume will delete all the previously generated recommendations. Do you still wish to proceed?"
        proceedText="Yes, proceed"
        onProceed={handleConfirmUploadResume}
        closeText="No, cancel"
        title="Upload different resume?"
        icon={<WarningCircle size={56} color="#212121" />}
      />
    </div>
  );
};

export default CplApplicantSubmittedProfile;